import { useState, useEffect, useCallback } from 'react';
import { useUser, useAuth } from '../contexts/AuthContext';
import { companyService } from '../services/api';

/**
 * Custom hook for company management  
 * Handles company state, loading, and operations
 * Updated to follow useProducts pattern exactly
 */
export const useCompany = () => {
  const { user } = useUser();
  const { refreshContext, applyCompanyUpdate } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Company state
  const [company, setCompany] = useState(null);
  const [hasCompany, setHasCompany] = useState(false);
  
  // Business type and accounting method options
  const [businessTypes, setBusinessTypes] = useState([]);
  const [accountingMethods, setAccountingMethods] = useState([]);

  // Check if user has a company (lightweight check)
  const checkCompanyStatus = useCallback(async () => {
    if (!user?.id) return;
    
    try {
      setLoading(true);
      setError(null);
      
      const response = await companyService.checkUserHasCompany();
      if (response.success) {
        setHasCompany(response.hasCompany);
      }
    } catch (err) {
      console.error('Failed to check company status:', err);
      setError('Failed to check company status');
      setHasCompany(false);
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  // Load company data with statistics
  const loadCompany = useCallback(async () => {
    if (!user?.id) return;
    
    try {
      setLoading(true);
      setError(null);
      
      const response = await companyService.getCurrentCompany();
      if (response.success) {
        setCompany(response.company);
        setHasCompany(true);
      } else {
        setCompany(null);
        setHasCompany(false);
      }
    } catch (err) {
      console.error('Failed to load company:', err);
      if (err.response?.status === 404) {
        setCompany(null);
        setHasCompany(false);
        setError(null); // Not an error, just no company
      } else {
        setError('Failed to load company information');
      }
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  // Load form options (business types and accounting methods)
  const loadFormOptions = useCallback(async () => {
    try {
      const [businessTypesResponse, accountingMethodsResponse] = await Promise.all([
        companyService.getBusinessTypes(),
        companyService.getAccountingMethods()
      ]);
      
      if (businessTypesResponse.success) {
        setBusinessTypes(businessTypesResponse.businessTypes);
      }
      
      if (accountingMethodsResponse.success) {
        setAccountingMethods(accountingMethodsResponse.accountingMethods);
      }
    } catch (err) {
      console.error('Failed to load form options:', err);
      // Don't set error for form options failure
    }
  }, []);

  // Create a new company
  const createCompany = useCallback(async (companyData) => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await companyService.createCompany(companyData);
      if (response.success) {
        setCompany(response.company);
        setHasCompany(true);
        return { success: true, company: response.company };
      }
    } catch (err) {
      console.error('Failed to create company:', err);
      const errorMessage = err.response?.data?.error || 'Failed to create company';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  }, []);

  // Update company
  const updateCompany = useCallback(async (updateData) => {
    try {
      setLoading(true);
      setError(null);

      const response = await companyService.updateCompany(updateData);
      if (response.success) {
        setCompany(response.company);
        // Push Settings save into AuthContext immediately so Create Invoice/Estimate
        // From fields update without a hard refresh (refreshContext is best-effort).
        applyCompanyUpdate?.(response.company);
        try {
          await refreshContext?.();
        } catch (refreshErr) {
          console.warn('Company updated but auth context refresh failed:', refreshErr);
        }
        return { success: true, company: response.company };
      }
      // Handle unexpected response format
      const errorMessage = response.error || 'Failed to update company';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } catch (err) {
      console.error('Failed to update company:', err);
      const errorMessage = err.response?.data?.error || 'Failed to update company';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  }, [refreshContext, applyCompanyUpdate]);

  // Refresh company data
  const refreshCompany = useCallback(() => {
    return loadCompany();
  }, [loadCompany]);

  // Initial load
  useEffect(() => {
    if (user?.id) {
      // Load company status first, then form options
      checkCompanyStatus();
      loadFormOptions();
    }
  }, [user?.id, checkCompanyStatus, loadFormOptions]);

  return {
    // State
    company,
    hasCompany,
    loading,
    error,
    businessTypes,
    accountingMethods,
    
    // Actions
    createCompany,
    updateCompany,
    loadCompany,
    refreshCompany,
    checkCompanyStatus,
    
    // Computed values
    isOnboarded: hasCompany && company !== null,
    needsOnboarding: !hasCompany,
    
    // Helper functions
    clearError: () => setError(null)
  };
};